import React, { useState, useRef, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, Animated, useWindowDimensions } from 'react-native';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import LighthouseKpiCard from '../LighthouseKpiCard';
import LighthouseKpiDetailModal from '../LighthouseKpiDetailModal';
import FontFamily from '../../../../constants/fonts';
import LHModalPop from '../../../../components/LHModalPop';
import { useLighthouseKpiFavorite } from '../../hooks/useLighthouseKpiFavorite';
import { getUnfavouriteModalConfig } from '../../constants/unfavouriteModalConfig';
import { StarIconOutline } from '../StandupIcons';

// Loading Skeleton Component for Lighthouse KPI Cards
const LighthouseKpiCardSkeleton: React.FC = () => {
  const opacity = useRef(new Animated.Value(0.3)).current;

  useEffect(() => {
    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, {
          toValue: 1,
          duration: 800,
          useNativeDriver: true,
        }),
        Animated.timing(opacity, {
          toValue: 0.3,
          duration: 800,
          useNativeDriver: true,
        }),
      ])
    );
    animation.start();
    return () => animation.stop();
  }, [opacity]);

  return (
    <View style={skeletonStyles.card}>
      {/* Header Skeleton */}
      <View style={skeletonStyles.header}>
        <Animated.View style={[skeletonStyles.title, { opacity }]} />
        <Animated.View style={[skeletonStyles.favorite, { opacity }]} />
      </View>

      {/* Metrics Row Skeleton */}
      <View style={skeletonStyles.metricsRow}>
        {[1, 2, 3, 4].map((_, index) => (
          <View key={index} style={skeletonStyles.metricItem}>
            <Animated.View style={[skeletonStyles.metricLabel, { opacity }]} />
            <Animated.View style={[skeletonStyles.metricValue, { opacity }]} />
          </View>
        ))}
      </View>

      {/* Chart Skeleton */}
      <View style={skeletonStyles.chart}>
        <View style={skeletonStyles.chartBars}>
          {[1, 2, 3, 4, 5, 6, 7].map((_, index) => (
            <Animated.View
              key={index}
              style={[
                skeletonStyles.chartBar,
                { height: 20 + Math.random() * 40, opacity },
              ]}
            />
          ))}
        </View>
      </View>
    </View>
  );
};

const skeletonStyles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E0E0E0',
    padding: 16,
    gap: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    width: '50%',
    height: 20,
    backgroundColor: '#E0E0E0',
    borderRadius: 4,
  },
  favorite: {
    width: 20,
    height: 20,
    backgroundColor: '#E0E0E0',
    borderRadius: 10,
  },
  metricsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 8,
  },
  metricItem: {
    flex: 1,
    gap: 4,
  },
  metricLabel: {
    width: '100%',
    height: 12,
    backgroundColor: '#E0E0E0',
    borderRadius: 4,
  },
  metricValue: {
    width: '80%',
    height: 24,
    backgroundColor: '#E0E0E0',
    borderRadius: 4,
  },
  chart: {
    height: 72,
    justifyContent: 'flex-end',
  },
  chartBars: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    height: '100%',
    gap: 4,
  },
  chartBar: {
    flex: 1,
    backgroundColor: '#E0E0E0',
    borderRadius: 2,
  },
});

interface LighthouseExecutionSectionProps {
  favoriteCards: string[];
  allFavoriteCards?: string[];
  onToggleFavorite: (cardId: string) => void;
  selectedPeriod: string;
  onPeriodChange?: (period: string) => void;
  lighthouseCardsData?: Array<{
    id: string;
    title: string;
    question_id?: number;
    is_parent?: boolean;
    cw: number;
    lcw: number;
    lcp: number;
    ytd: number;
    chartData: Array<{ date: string; value: number }>;
  }>;
  selectedLocation: { id: string; displayText: string; } | null;
  isLoading?: boolean;
  onCreateActionPlan?: (card: any) => void;
  showEmptyState?: boolean; // new optional prop to avoid duplicate messages
  metricTrendData?: any;
}

export const LighthouseExecutionSection: React.FC<LighthouseExecutionSectionProps> = ({ 
  favoriteCards, 
  allFavoriteCards, 
  onToggleFavorite, 
  selectedPeriod, 
  onPeriodChange,
  lighthouseCardsData = [],
  selectedLocation,
  isLoading = false,
  onCreateActionPlan,
  showEmptyState = true,
  metricTrendData,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState<any | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { width: windowWidth } = useWindowDimensions();

  // Unfavourite confirmation modal state
  const [showUnfavouriteModal, setShowUnfavouriteModal] = useState(false);
  const [pendingUnfavouriteCardId, setPendingUnfavouriteCardId] = useState<string | null>(null);
  const [pendingUnfavouriteCardTitle, setPendingUnfavouriteCardTitle] = useState<string | null>(null);

  // Get hook for API calls
  const { toggleFavorite: toggleLighthouseFavorite } = useLighthouseKpiFavorite();

  // Calculate responsive card width based on screen size - matching new responsive approach
  // Start with 4 cards per row, then increase to 5, 6+ as screen gets wider
  const getCardWidth = () => {
    if (windowWidth >= 1600) return 'calc(20% - 10px)'; // 5 columns for very wide
    if (windowWidth >= 1900) return 'calc(16.666% - 10px)'; // 6 columns for ultra-wide
    if (windowWidth >= 1400) return 'calc(25% - 9px)'; // 4 columns (default wide)
    if (windowWidth >= 1024) return 'calc(33.333% - 8px)'; // 3 columns
    if (windowWidth >= 768) return 'calc(50% - 6px)'; // 2 columns
    return '100%'; // 1 column for mobile
  };

  const getMinWidth = () => {
    if (windowWidth >= 1400) return 250;
    if (windowWidth >= 768) return 0;
    return 0;
  };

  // Get favorites from Redux
  const favoritesFromRedux = useSelector(
    (state: RootState) => state.standupRedirect.favorites.data
  );

  // Read lighthouse execution error from Redux and show inline empty-state when present
  const lighthouseExecutionError = useSelector(
    (state: RootState) => state.standupRedirect.reviewPerformance.lighthouseExecution.error
  );

  // Map lighthouse title to item_id for Redux lookup
  const titleToItemId: { [key: string]: string } = {
    'Pulse Check': 'pulse_check',
    'Observation': 'observation',
    'Shift Handover': 'shift_handover',
    'Sparc Walk': 'sparc_walk',
    'Leadership Walk': 'leadership_walk',
  };

  // Helper function to check if a Lighthouse KPI is favorited
  const isLighthouseKpiFavorited = useCallback((cardTitle: string): boolean => {
    const favoritesArray = Array.isArray(favoritesFromRedux) 
      ? favoritesFromRedux 
      : (favoritesFromRedux as any)?.data || [];
    
    const itemId = titleToItemId[cardTitle] || cardTitle.toLowerCase().replace(/\s+/g, '_');
    
    return favoritesArray.some(
      (fav: any) => fav.item_type === 'lighthouse_metric' && fav.item_id === itemId && fav.is_favourite === true
    );
  }, [favoritesFromRedux]);

  const handleCardClick = (card: any) => {
    setSelectedCard(card);
    setIsModalVisible(true);
  };

  const handleModalDismiss = () => {
    setIsModalVisible(false);
    setSelectedCard(null);
  };

  const handleCreateActionPlan = () => {
    // Handle action plan creation - call parent callback with selected card
    if (onCreateActionPlan && selectedCard) {
      onCreateActionPlan(selectedCard);
    }
    setIsModalVisible(false);
  };

  // Handle favorite toggle with modal confirmation for unfavourite
  const handleLighthouseToggleFavorite = (cardId: string) => {
    const card = lighthouseCardsData.find(c => c.id === cardId);
    if (!card) return;

    const isFavorited = isLighthouseKpiFavorited(card.title);

    if (!isFavorited) {
      // Adding favorite - immediate action
      onToggleFavorite(cardId);
    } else {
      // Removing favorite - show confirmation modal
      setPendingUnfavouriteCardId(cardId);
      setPendingUnfavouriteCardTitle(card.title);
      setShowUnfavouriteModal(true);
    }
  };

  // Handle confirmed unfavourite
  const handleConfirmUnfavourite = async () => {
    if (!pendingUnfavouriteCardId || !pendingUnfavouriteCardTitle) return;

    // Call API hook to unfavourite
    await toggleLighthouseFavorite(pendingUnfavouriteCardTitle);

    // Close modal and reset
    setShowUnfavouriteModal(false);
    setPendingUnfavouriteCardId(null);
    setPendingUnfavouriteCardTitle(null);
  };

  // Handle cancel unfavourite
  const handleCancelUnfavourite = () => {
    setShowUnfavouriteModal(false);
    setPendingUnfavouriteCardId(null);
    setPendingUnfavouriteCardTitle(null);
  };

  const modalConfig = getUnfavouriteModalConfig('review-performance');

  return (
    <View style={styles.sparcKpiListSection}>
      {/* Unfavourite Confirmation Modal */}
      <LHModalPop
        visible={showUnfavouriteModal}
        onClose={handleCancelUnfavourite}
        title={modalConfig.header}
        showFooter={true}
        primaryButtonText={modalConfig.primaryButton}
        secondaryButtonText={modalConfig.secondaryButton}
        onPrimaryPress={handleConfirmUnfavourite}
        onSecondaryPress={handleCancelUnfavourite}
        showCloseButton={true}
      >
        <View style={{ gap: 12, paddingVertical: 8 }}>
          <Text style={{ fontSize: 14, color: '#3A3A3A', fontFamily: FontFamily.regular, lineHeight: 20 }}>
            {modalConfig.bodyLine1}
          </Text>
          <Text style={{ fontSize: 14, color: '#3A3A3A', fontFamily: FontFamily.regular, lineHeight: 20 }}>
            {modalConfig.bodyLine2}
          </Text>
        </View>
      </LHModalPop>

      {/* Main Header with Print Button */}
      <View style={styles.sparcKpiListMainHeader}>
        <Text style={styles.sparcKpiListMainTitle}>Lighthouse Execution</Text>
      </View>

      {/* Board Container */}
      <View style={styles.LighthouseContainer}>
        {/* Lighthouse Cards Row */}
        <View style={styles.sparcCardsRow}>
          {isLoading ? (
            // Show skeleton loading cards
            [...Array(4)].map((_, index) => (
              <View key={`skeleton-${index}`} style={styles.sparcCardEqualWrapper}>
                <LighthouseKpiCardSkeleton />
              </View>
            ))
          ) : (showEmptyState && (lighthouseExecutionError || (Array.isArray(lighthouseCardsData) && lighthouseCardsData.length === 0))) ? (
            // Show same simple empty text used by SparcKpiListSection
            <Text style={styles.emptyText}>No Lighthouse execution cards available</Text>
          ) : (
            // Show actual data
            lighthouseCardsData.map((card) => (
              <View 
              key={card.id} 
              style={[
                styles.sparcCardEqualWrapper,
                { 
                  flexBasis: getCardWidth() as any,
                  maxWidth: getCardWidth() as any,
                  minWidth: getMinWidth(),
                }
              ]}
            >
                <LighthouseKpiCard
                  title={card.title}
                  cw={card.cw}
                  lcw={card.lcw}
                  lcp={card.lcp}
                  ytd={card.ytd}
                  chartData={card.chartData}
                  isFavorite={isLighthouseKpiFavorited(card.title)}
                  onFavoritePress={() => handleLighthouseToggleFavorite(card.id)}
                  disableApiCall={isLighthouseKpiFavorited(card.title)}
                  onCardPress={() => handleCardClick(card)}
                />
              </View>
            ))
          )}
        </View>
      </View>

      {/* Detail Modal */}
      <LighthouseKpiDetailModal
        visible={isModalVisible}
        onDismiss={handleModalDismiss}
        onCreateActionPlan={handleCreateActionPlan}
        cardData={selectedCard}
        isFavorite={selectedCard ? favoriteCards.includes(selectedCard.id) : false}
        onToggleFavorite={() => selectedCard && onToggleFavorite(selectedCard.id)}
        selectedLocation={selectedLocation}
        metricTrendData={metricTrendData}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  sparcKpiListSection: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    marginTop: 24,
    marginBottom: 20,
    paddingHorizontal: 24,
    paddingVertical: 24,
    overflow: 'visible',
  },
  // same simple empty text used in SPARC KPI list
  emptyText: {
    width: '100%',
    paddingVertical: 20,
    textAlign: 'left' as any,
    fontSize: 14,
    fontWeight: '400',
    color: '#616161',
    fontFamily: FontFamily.regular,
  },
  sparcKpiListMainHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  sparcKpiListMainTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#2D2D2D',
    fontFamily: FontFamily.bold,
  },
  LighthouseContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    overflow: 'visible',
  },
  sparcCardsRow: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  sparcCardEqualWrapper: {
    flexBasis: 'calc(25% - 9px)' as any,
    minWidth: 0,
    maxWidth: 'calc(25% - 9px)' as any,
  },
});